import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Camera, X, MapPin, Plus, Coins, Star, Clock } from "lucide-react";
import { ObjectUploader } from "@/components/ObjectUploader";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { UploadResult } from '@uppy/core';

interface PhotoCaptureModalProps {
  isOpen: boolean;
  onClose: () => void;
  userLocation: { lat: number; lng: number } | null;
}

export default function PhotoCaptureModal({ isOpen, onClose, userLocation }: PhotoCaptureModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    imageUrl: '',
    emojiPin: '📍',
    userRole: 'photographer' as 'photographer' | 'artist',
    isFreshArt: false,
  });
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const emojiPins = [
    { emoji: "🏷️", name: "Tag", description: "Tagging spots" },
    { emoji: "📍", name: "Location", description: "General marker" },
    { emoji: "🔻", name: "Marker", description: "Important point" },
    { emoji: "🚩", name: "Flag", description: "Special location" },
    { emoji: "⚡", name: "Hot Spot", description: "High activity" }
  ];

  const createDiscoveryMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await apiRequest("POST", "/api/discoveries", data);
      return response.json();
    },
    onSuccess: (data) => {
      const tokensEarned = data.tokensAwarded || 50;
      const roleType = formData.userRole === 'artist' ? 'Artist' : 'Photographer';
      
      toast({
        title: "Discovery Added! 🎉",
        description: `+${tokensEarned} 🪙 Spray Coins earned as ${roleType}! Your ${formData.emojiPin} pin has been placed.`,
      });
      queryClient.invalidateQueries({ queryKey: ["/api/discoveries/user"] });
      queryClient.invalidateQueries({ queryKey: ["/api/discoveries/nearby"] });
      queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] }); // Refresh user data for token update
      onClose();
      setFormData({ 
        name: '', 
        description: '', 
        imageUrl: '', 
        emojiPin: '📍',
        userRole: 'photographer',
        isFreshArt: false,
      });
    },
    onError: (error) => {
      console.error("Error creating discovery:", error);
      toast({
        title: "Error",
        description: "Failed to save discovery. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleGetUploadParameters = async () => {
    try {
      const response = await apiRequest("POST", "/api/objects/upload");
      const data = await response.json();
      return {
        method: 'PUT' as const,
        url: data.uploadURL,
      };
    } catch (error) {
      console.error("Error getting upload parameters:", error);
      throw error;
    }
  };

  const handleUploadComplete = (result: UploadResult<Record<string, unknown>, Record<string, unknown>>) => {
    if (result.successful && result.successful.length > 0) {
      const uploadedFile = result.successful[0];
      if (uploadedFile.uploadURL) {
        setFormData(prev => ({ ...prev, imageUrl: uploadedFile.uploadURL as string }));
        setIsUploading(false);
      }
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast({
        title: "Missing Information",
        description: "Please enter a name for your discovery.",
        variant: "destructive",
      });
      return;
    }

    if (!userLocation) {
      toast({
        title: "Location Required",
        description: "Location access is required to save discoveries.",
        variant: "destructive",
      });
      return;
    }

    const tokensAwarded = formData.userRole === 'artist' ? 150 : 50;
    const currentTime = new Date().toISOString();

    createDiscoveryMutation.mutate({
      ...formData,
      latitude: userLocation.lat,
      longitude: userLocation.lng,
      address: `${userLocation.lat.toFixed(6)}, ${userLocation.lng.toFixed(6)}`,
      tokensAwarded,
      metadata: {
        timestamp: currentTime,
        coordinates: `${userLocation.lat},${userLocation.lng}`,
        submissionType: formData.userRole === 'artist' ? 'fresh_artwork' : 'discovery',
        emojiType: formData.emojiPin,
      }
    });
  };

  const resetAndClose = () => {
    setFormData({ 
      name: '', 
      description: '', 
      imageUrl: '', 
      emojiPin: '📍',
      userRole: 'photographer',
      isFreshArt: false,
    });
    setIsUploading(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={resetAndClose}>
      <DialogContent className="bg-dark-elevated border-gray-700 text-white max-w-md" data-testid="photo-capture-modal">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl font-semibold" data-testid="modal-title">Document Street Art</DialogTitle>
            <Button variant="ghost" size="sm" onClick={resetAndClose} className="text-gray-400" data-testid="button-close-modal">
              <X size={20} />
            </Button>
          </div>
        </DialogHeader>
        
        {/* Camera Preview Area */}
        <div className="bg-dark-surface rounded-xl mb-4 aspect-square flex items-center justify-center border-2 border-dashed border-gray-600">
          {formData.imageUrl ? (
            <div className="relative w-full h-full">
              <img 
                src={formData.imageUrl} 
                alt="Uploaded discovery" 
                className="w-full h-full object-cover rounded-xl"
                data-testid="uploaded-image"
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, imageUrl: '' }))}
                className="absolute top-2 right-2 bg-dark-base/80 hover:bg-dark-base"
                data-testid="button-remove-image"
              >
                <X size={16} />
              </Button>
            </div>
          ) : (
            <div className="text-center">
              {isUploading ? (
                <div data-testid="upload-loading">
                  <div className="animate-spin w-8 h-8 border-4 border-neon-green border-t-transparent rounded-full mx-auto mb-2" />
                  <p className="text-gray-400">Uploading...</p>
                </div>
              ) : (
                <>
                  <Camera className="text-4xl text-gray-400 mb-2" size={48} />
                  <p className="text-gray-400 mb-4" data-testid="upload-instruction">Upload a photo of the street art</p>
                  <ObjectUploader
                    maxNumberOfFiles={1}
                    maxFileSize={10485760}
                    onGetUploadParameters={handleGetUploadParameters}
                    onComplete={handleUploadComplete}
                    buttonClassName="bg-neon-green text-dark-base hover:bg-neon-green/90"
                  >
                    <Camera className="mr-2" size={16} />
                    Choose Photo
                  </ObjectUploader>
                </>
              )}
            </div>
          )}
        </div>
        
        {/* Photo Details Form */}
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="discovery-form">
          <div>
            <Input 
              type="text" 
              placeholder="Art piece name" 
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="bg-dark-surface border-gray-600 text-white placeholder-gray-400 focus:border-neon-green"
              data-testid="input-art-name"
            />
          </div>
          <div>
            <Textarea 
              placeholder="Description and location details..." 
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              className="bg-dark-surface border-gray-600 text-white placeholder-gray-400 focus:border-neon-green resize-none"
              data-testid="textarea-description"
            />
          </div>

          {/* Emoji Pin Selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-300">Choose Pin Type</label>
            <div className="grid grid-cols-5 gap-2">
              {emojiPins.map((pin) => (
                <Button
                  key={pin.emoji}
                  type="button"
                  variant={formData.emojiPin === pin.emoji ? "default" : "outline"}
                  size="sm"
                  onClick={() => setFormData(prev => ({ ...prev, emojiPin: pin.emoji }))}
                  className={`p-3 h-auto flex flex-col items-center gap-1 ${
                    formData.emojiPin === pin.emoji 
                      ? 'bg-neon-green text-dark-base' 
                      : 'bg-dark-surface border-gray-600 text-gray-400 hover:bg-dark-elevated'
                  }`}
                  data-testid={`emoji-pin-${pin.emoji}`}
                >
                  <span className="text-lg">{pin.emoji}</span>
                  <span className="text-xs">{pin.name}</span>
                </Button>
              ))}
            </div>
          </div>

          {/* Role Selection */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-gray-300">Your Role</label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                type="button"
                variant={formData.userRole === 'photographer' ? "default" : "outline"}
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, userRole: 'photographer' }))}
                className={`p-3 h-auto flex flex-col items-center gap-2 ${
                  formData.userRole === 'photographer' 
                    ? 'bg-coral text-white' 
                    : 'bg-dark-surface border-gray-600 text-gray-400 hover:bg-dark-elevated'
                }`}
                data-testid="role-photographer"
              >
                <Camera size={20} />
                <div className="text-center">
                  <div className="text-sm font-medium">Photographer</div>
                  <div className="text-xs opacity-75">50 🪙 Spray Coins</div>
                </div>
              </Button>
              <Button
                type="button"
                variant={formData.userRole === 'artist' ? "default" : "outline"}
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, userRole: 'artist' }))}
                className={`p-3 h-auto flex flex-col items-center gap-2 ${
                  formData.userRole === 'artist' 
                    ? 'bg-neon-green text-dark-base' 
                    : 'bg-dark-surface border-gray-600 text-gray-400 hover:bg-dark-elevated'
                }`}
                data-testid="role-artist"
              >
                <Star size={20} />
                <div className="text-center">
                  <div className="text-sm font-medium">Artist</div>
                  <div className="text-xs opacity-75">150 🪙 Spray Coins</div>
                </div>
              </Button>
            </div>
          </div>

          {/* Fresh Art Toggle for Artists */}
          {formData.userRole === 'artist' && (
            <div className="space-y-2">
              <Button
                type="button"
                variant={formData.isFreshArt ? "default" : "outline"}
                size="sm"
                onClick={() => setFormData(prev => ({ ...prev, isFreshArt: !prev.isFreshArt }))}
                className={`w-full p-3 h-auto flex items-center gap-3 ${
                  formData.isFreshArt 
                    ? 'bg-yellow-600 text-white' 
                    : 'bg-dark-surface border-gray-600 text-gray-400 hover:bg-dark-elevated'
                }`}
                data-testid="fresh-art-toggle"
              >
                <Clock size={20} />
                <div className="text-left">
                  <div className="text-sm font-medium">Fresh Artwork Proof</div>
                  <div className="text-xs opacity-75">Verify this is newly created art with timestamp 🚩📷🧤</div>
                </div>
              </Button>
            </div>
          )}

          <Card className="bg-dark-surface border-gray-600">
            <CardContent className="pt-3">
              <div className="flex items-center">
                <MapPin className="text-teal-custom mr-3" size={20} />
                <span className="text-sm" data-testid="current-location">
                  {userLocation 
                    ? `${userLocation.lat.toFixed(6)}, ${userLocation.lng.toFixed(6)}`
                    : 'Location unavailable'
                  }
                </span>
              </div>
            </CardContent>
          </Card>
          
          <Button 
            type="submit" 
            disabled={createDiscoveryMutation.isPending || isUploading}
            className="w-full bg-neon-green text-dark-base font-semibold hover:bg-neon-green/90 disabled:opacity-50"
            data-testid="button-add-discovery"
          >
            {createDiscoveryMutation.isPending ? (
              <>
                <div className="animate-spin w-4 h-4 border-2 border-dark-base border-t-transparent rounded-full mr-2" />
                Adding...
              </>
            ) : (
              <>
                <Plus className="mr-2" size={16} />
                Add to Discoveries
              </>
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
